import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { API_BASE } from '../ticketConstants';

/**
 * useOrderSignal — calls `onSignal(changedAtMs)` whenever Square's webhook reports
 * an order change, so a board can refetch at once instead of waiting for its next
 * poll. `changedAtMs` (server time of the change, or null) lets the refetch skip a
 * server-side cache that predates it — see `after` in api/square-tickets.js.
 *
 * PRIMARY → a Firestore listener on the webhook's tiny signal docs,
 *   liveOrdersSignal/{YYYY-MM-DD}. api/square-webhook writes that doc on every
 *   order event (keyed by the event's UTC date) together with _signal/latest —
 *   so this sees every change the poll saw, pushed instantly. It costs one read
 *   per webhook event per open screen.
 *
 * FALLBACK → if the listener is refused (signed out, rules, quota) the old
 *   behaviour: poll /api/order-signal every 5 s. The push is retried later.
 *
 * Why: the 5 s poll cost one Firestore read per 5 s per edge cache (per screen
 * where the cache misses, and in local dev) whether or not anything happened —
 * up to ~17k reads a day for a single board, a third of the free-tier quota.
 */

/** How often the fallback polls /api/order-signal. */
export const SIGNAL_POLL_MS = 5000;
/** How long to stay on the fallback before trying the push listener again. */
export const PUSH_RETRY_MS = 5 * 60 * 1000;
/**
 * Boards' `coalesceMs`: one order change sends several webhook events within a
 * second or two (created, updated, fulfillment updated…). Each used to trigger
 * its own refetch — and, carrying a newer change time, its own Square call past
 * the server cache. Signals this close together become one refetch.
 */
export const SIGNAL_COALESCE_MS = 1500;

/**
 * Signal doc ids to watch. The webhook keys each event by its own UTC date;
 * yesterday's doc is watched too so a device whose clock sits a little either
 * side of UTC midnight (7 PM CDT) never misses the roll-over.
 */
export function signalKeys(now = new Date()) {
  const today = now.toISOString().slice(0, 10);
  const yesterday = new Date(now.getTime() - 86400000).toISOString().slice(0, 10);
  return [today, yesterday];
}

/**
 * Returns { proven }: true once this board has received at least one real
 * signal (by push or by the fallback poll) — proof that webhooks reach it, which
 * useLiveOrders uses to relax its backup poll. It stays true for the session.
 *
 * `coalesceMs` (optional): signals arriving within this long of the first are
 * merged into one onSignal call, with the latest change time. 0 = call at once.
 */
export function useOrderSignal(enabled, onSignal, { coalesceMs = 0 } = {}) {
  const onSignalRef = useRef(onSignal);
  onSignalRef.current = onSignal;
  const coalesceRef = useRef(coalesceMs);
  coalesceRef.current = coalesceMs;
  const [keys, setKeys] = useState(() => signalKeys());
  const [pushFailed, setPushFailed] = useState(false);
  const [proven, setProven] = useState(false);
  const keyStr = keys.join('|');

  // Deliver a signal — at once, or merged with the rest of its burst.
  const burstRef = useRef({ timer: null, ts: null });
  const fire = useCallback((ts) => {
    setProven(true);
    const wait = coalesceRef.current;
    if (!wait) { onSignalRef.current(ts); return; }
    const b = burstRef.current;
    if (Number.isFinite(ts) && (b.ts == null || ts > b.ts)) b.ts = ts;
    if (b.timer) return;
    b.timer = setTimeout(() => {
      const latest = b.ts;
      b.timer = null;
      b.ts = null;
      onSignalRef.current(latest);
    }, wait);
  }, []);
  useEffect(() => () => { clearTimeout(burstRef.current.timer); burstRef.current.timer = null; }, []);

  // Roll the watched docs over at the UTC date change.
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => setKeys(prev => {
      const next = signalKeys();
      return next.join('|') === prev.join('|') ? prev : next;
    });
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [enabled]);

  // Primary: push.
  useEffect(() => {
    if (!enabled || pushFailed) return undefined;
    const seen = {};   // key → last server fingerprint; absent until the baseline lands
    const unsubs = keyStr.split('|').map(key => onSnapshot(
      doc(db, 'liveOrdersSignal', key),
      // Needed for the baseline: when the cached copy is already current, the
      // server's confirmation changes only metadata (fromCache → false) and is
      // not delivered by default — the baseline would never be set, and the
      // first real webhook would be swallowed as the baseline instead.
      { includeMetadataChanges: true },
      snap => {
        // Baseline from the server only — a cached copy is stale by definition,
        // and treating the cache→server hop as a change would refetch on mount.
        if (snap.metadata.fromCache) return;
        const data = snap.exists() ? snap.data() : null;
        const ts = data && data.updatedAt && typeof data.updatedAt.toMillis === 'function'
          ? data.updatedAt.toMillis() : null;
        // Two events inside one millisecond still differ by order/event/state.
        const mark = data ? `${ts}|${data.lastOrderId}|${data.lastEventType}|${data.lastOrderState}` : null;
        if (key in seen && mark !== seen[key]) fire(ts);
        seen[key] = mark;
      },
      () => setPushFailed(true),
    ));
    return () => unsubs.forEach(u => u());
  }, [enabled, pushFailed, keyStr, fire]);

  // Fallback: the old 5 s poll, until the push is retried.
  useEffect(() => {
    if (!enabled || !pushFailed) return undefined;
    const retry = setTimeout(() => setPushFailed(false), PUSH_RETRY_MS);
    if (!API_BASE) return () => clearTimeout(retry);
    let last = null;
    const check = async () => {
      try {
        const res = await window.fetch(`${API_BASE}/api/order-signal`);
        if (!res.ok) return;
        const { ts } = await res.json();
        if (!ts) return;
        if (last !== null && ts !== last) fire(typeof ts === 'number' ? ts : null);
        last = ts;
      } catch {}
    };
    check();
    const id = setInterval(check, SIGNAL_POLL_MS);
    return () => { clearInterval(id); clearTimeout(retry); };
  }, [enabled, pushFailed, fire]);

  return { proven };
}
